import type Luminary from '@lumen5/luminary';
import type { Asset } from '@lumen5/luminary';

/**
 * Walks the scene sequence from the focused asset and returns a map of sceneId → assetId
 * for all linked assets. Includes the focused asset itself only if the chain has
 * at least one other member. Each asset morphs to exactly 1 asset in an adjacent scene.
 */
export function getSceneSequenceMap(
    luminary: Readonly<Luminary>,
    focusedSceneId: string,
    focusedAssetId: string,
): Map<string, string> {
    const chain = new Map<string, string>();

    const focusedScene = luminary.getSceneById(focusedSceneId);
    if (!focusedScene) {
        return chain;
    }

    const focusedAsset = focusedScene.getAssetById(focusedAssetId);
    if (!focusedAsset) {
        return chain;
    }

    const isLinkedMatch = (asset: Asset) =>
        !asset.isMatchCutDuplicate && asset.isMorphMatch(focusedAsset);

    // Walk forward
    let current = focusedScene;
    while (current.morphEnabled && current.nextScene) {
        const nextScene = current.nextScene;
        const match = nextScene.getAssets().find(isLinkedMatch);
        if (!match) {
            break;
        }
        chain.set(nextScene.id, match.id);
        current = nextScene;
    }

    // Walk backward
    current = focusedScene;
    while (current.prevScene?.morphEnabled) {
        const prevScene = current.prevScene;
        const match = prevScene.getAssets().find(isLinkedMatch);
        if (!match) {
            break;
        }
        chain.set(prevScene.id, match.id);
        current = prevScene;
    }

    // Include the focused asset itself only if the chain has other members
    if (chain.size > 0) {
        chain.set(focusedSceneId, focusedAssetId);
    }

    return chain;
}
